import Swal from 'sweetalert2';
import type { StaffMember } from '@/mocks/adminStaff';
import { roleLabels, roleColors, statusLabels, statusColors } from '@/mocks/adminStaff';

interface StaffTableProps {
  staff: StaffMember[];
  onEdit: (member: StaffMember) => void;
  onDelete: (member: StaffMember) => void;
  onSuspend: (member: StaffMember) => void;
  onReactivate: (member: StaffMember) => void;
  onResendInvite: (member: StaffMember) => void;
}

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
  showConfirmButton: false,
  showCloseButton: true,
});

function showActionsModal(
  member: StaffMember,
  handlers: {
    onEdit: (m: StaffMember) => void;
    onDelete: (m: StaffMember) => void;
    onSuspend: (m: StaffMember) => void;
    onReactivate: (m: StaffMember) => void;
    onResendInvite: (m: StaffMember) => void;
  }
) {
  const actionBtn = (icon: string, label: string, color: string, hoverBg: string) =>
    `<button type="button" data-action="${label.toLowerCase().replace(/\s/g, '-')}"
      class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ${color} ${hoverBg}">
      <span class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style="background:currentColor;opacity:0.1">
        <i class="${icon} text-base" style="color:currentColor"></i>
      </span>
      ${label}
    </button>`;

  const actions: string[] = [];

  actions.push(actionBtn('ri-pencil-line', 'Editar permisos', 'text-foreground-700', 'hover:bg-background-100'));

  if (member.status === 'invited') {
    actions.push(actionBtn('ri-mail-send-line', 'Reenviar invitación', 'text-amber-600', 'hover:bg-amber-50'));
  }

  if (member.status === 'active' || member.status === 'invited') {
    actions.push(actionBtn('ri-pause-circle-line', 'Suspender acceso', 'text-orange-600', 'hover:bg-orange-50'));
  }

  if (member.status === 'suspended' || member.status === 'inactive') {
    actions.push(actionBtn('ri-play-circle-line', 'Reactivar acceso', 'text-emerald-600', 'hover:bg-emerald-50'));
  }

  actions.push('<div class="border-t border-background-200 my-1"></div>');
  actions.push(actionBtn('ri-delete-bin-line', 'Eliminar acceso', 'text-red-600', 'hover:bg-red-50'));

  sharedSwal
    .fire({
      title: member.name,
      html: `
        <div class="text-left space-y-1">
          <p class="text-xs text-foreground-400 mb-1">${escapeHtml(member.email)}</p>
          <div class="flex items-center gap-2 mb-3">
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold ${roleColors[member.role]}">
              ${roleLabels[member.role]}
            </span>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColors[member.status]}">
              <span class="w-1.5 h-1.5 rounded-full ${
                member.status === 'active' ? 'bg-emerald-500' :
                member.status === 'invited' ? 'bg-amber-500' :
                member.status === 'suspended' ? 'bg-red-500' :
                'bg-foreground-400'
              }"></span>
              ${statusLabels[member.status]}
            </span>
          </div>
          ${actions.join('')}
        </div>
      `,
      customClass: {
        popup: 'rounded-2xl max-w-sm',
        title: 'font-bold text-sm',
        htmlContainer: '!p-0',
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (!popup) return;

        popup.querySelector('[data-action="editar-permisos"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onEdit(member);
        });
        popup.querySelector('[data-action="reenviar-invitación"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onResendInvite(member);
        });
        popup.querySelector('[data-action="suspender-acceso"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onSuspend(member);
        });
        popup.querySelector('[data-action="reactivar-acceso"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onReactivate(member);
        });
        popup.querySelector('[data-action="eliminar-acceso"]')?.addEventListener('click', () => {
          Swal.close();
          handlers.onDelete(member);
        });
      },
    });
}

export default function StaffTable({ staff, onEdit, onDelete, onSuspend, onReactivate, onResendInvite }: StaffTableProps) {
  if (staff.length === 0) {
    return (
      <div className="hidden lg:block bg-background-50 rounded-2xl border border-background-200">
        <div className="text-center py-14">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
            <i className="ri-user-search-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-600">Sin resultados</p>
          <p className="text-xs text-foreground-400 mt-1">No hay miembros que coincidan con los filtros.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden lg:block bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-background-200 bg-background-100/60">
            <th className="text-left px-5 py-3.5 text-[11px] font-bold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Staff</th>
            <th className="text-left px-5 py-3.5 text-[11px] font-bold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Rol</th>
            <th className="text-left px-5 py-3.5 text-[11px] font-bold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Estado</th>
            <th className="text-left px-5 py-3.5 text-[11px] font-bold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Permisos</th>
            <th className="w-16 px-5 py-3.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-background-200/70">
          {staff.map((member) => (
            <tr key={member.id} className="hover:bg-background-50/80 transition-colors">
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-background-200 overflow-hidden shrink-0">
                    <img
                      src={member.photoUrl}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 truncate">{member.name}</p>
                    <p className="text-[11px] text-foreground-400 truncate">{member.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-5 py-4">
                <span className={`inline-block px-2.5 py-1 rounded-lg text-[11px] font-bold whitespace-nowrap ${roleColors[member.role]}`}>
                  {roleLabels[member.role]}
                </span>
              </td>
              <td className="px-5 py-4">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${statusColors[member.status]}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    member.status === 'active' ? 'bg-emerald-500' :
                    member.status === 'invited' ? 'bg-amber-500' :
                    member.status === 'suspended' ? 'bg-red-500' :
                    'bg-foreground-400'
                  }`}></span>
                  {statusLabels[member.status]}
                </span>
                {member.status === 'suspended' && member.suspensionReason && (
                  <p className="text-[10px] text-red-500 mt-1 max-w-[160px] truncate" title={member.suspensionReason}>
                    {member.suspensionReason}
                  </p>
                )}
              </td>
              <td className="px-5 py-4">
                <div className="flex items-center gap-1 flex-wrap max-w-[200px]">
                  {member.permissions.length > 0 ? member.permissions.map((perm) => (
                    <span key={perm} className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-background-100 text-foreground-500 whitespace-nowrap">
                      {perm}
                    </span>
                  )) : (
                    <span className="text-[10px] text-foreground-400 italic">Sin permisos</span>
                  )}
                </div>
              </td>
              <td className="px-5 py-4 text-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    showActionsModal(member, { onEdit, onDelete, onSuspend, onReactivate, onResendInvite });
                  }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
                  title="Acciones"
                >
                  <i className="ri-more-2-fill text-lg"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}